import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import ridePromoCodeApi from 'api/ridePromoCodeApi';
import KT01BaseLayout from 'general/components/BaseLayout/KT01BaseLayout';
import ToastHelper from 'general/helpers/ToastHelper';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';

const { Text, Title } = Typography;
const money = (value) => value == null ? '—' : `${Number(value).toLocaleString('vi-VN')} ₫`;
const statusTag = (value) => value === 'ACTIVE'
  ? <Tag color="success">Đang bật</Tag>
  : <Tag color="default">Đã tắt</Tag>;

const defaults = {
  code: '', description: '', discountType: 'PERCENT', discountPercent: 10, discountAmount: null,
  maxDiscountAmount: null, targetFare: null, routeScope: 'ALL', minTotalFare: 0, usageLimit: null, perCustomerLimit: null,
  startAt: null, endAt: null, isPublic: false,
};

function RidePromoCodes() {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const discountType = Form.useWatch('discountType', form) || 'PERCENT';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ridePromoCodeApi.getPromoCodes({ pageSize: 100 });
      const now = dayjs();
      setItems((response?.items || []).filter((item) => !item.endAt || !dayjs(item.endAt).isBefore(now)));
    }
    catch (error) { setItems([]); ToastHelper.showError(error?.response?.data?.message || 'Không thể tải mã khuyến mãi'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const showCreate = () => { setEditing(null); form.setFieldsValue(defaults); setOpen(true); };
  const showEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      ...defaults, ...row,
      startAt: row.startAt ? dayjs(row.startAt) : null,
      endAt: row.endAt ? dayjs(row.endAt) : null,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        // Ant Design may return null or undefined after InputNumber is
        // cleared. Always send an explicit null so the API persists
        // "không giới hạn" instead of applying any database default.
        perCustomerLimit: values.perCustomerLimit == null ? null : values.perCustomerLimit,
        startAt: values.startAt ? values.startAt.toISOString() : null,
        endAt: values.endAt ? values.endAt.toISOString() : null,
      };
      if (editing) await ridePromoCodeApi.updatePromoCode(editing.promoCodeId, payload);
      else await ridePromoCodeApi.createPromoCode(payload);
      ToastHelper.showSuccess(editing ? 'Đã cập nhật mã khuyến mãi' : 'Đã tạo mã khuyến mãi');
      setOpen(false); await load();
    } catch (error) {
      if (!error?.errorFields) ToastHelper.showError(error?.response?.data?.message || 'Không thể lưu mã khuyến mãi');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (row) => {
    try {
      await ridePromoCodeApi.setPromoCodeStatus(row.promoCodeId, row.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE');
      ToastHelper.showSuccess(row.status === 'ACTIVE' ? 'Đã tắt mã khuyến mãi' : 'Đã bật mã khuyến mãi');
      await load();
    } catch (error) { ToastHelper.showError(error?.response?.data?.message || 'Không thể đổi trạng thái'); }
  };

  const columns = [
    { title: 'Mã', render: (_, row) => <div><Text strong>{row.code}</Text><br /><Text type="secondary">{row.description || '—'}</Text></div> },
    {
      title: 'Giảm giá',
      render: (_, row) => row.discountType === 'PERCENT'
        ? <span>{row.discountPercent}%{row.maxDiscountAmount ? <><br /><Text type="secondary">Tối đa {money(row.maxDiscountAmount)}</Text></> : null}</span>
        : row.discountType === 'TARGET_FARE'
          ? <span>Còn {money(row.targetFare)}</span>
          : <span>{money(row.discountAmount)}</span>,
    },
    {
      title: 'Phạm vi tuyến',
      dataIndex: 'routeScope',
      render: (value) => value === 'HANOI_OLD_NAM_DINH' ? 'Hà Nội ↔ Nam Định cũ' : 'Mọi tuyến',
    },
    { title: 'Đơn tối thiểu', dataIndex: 'minTotalFare', render: money },
    {
      title: 'Đã dùng / Giới hạn',
      render: (_, row) => `${row.usageCount}/${row.usageLimit ?? '∞'} · mỗi khách ${row.perCustomerLimit ?? '∞'}`,
    },
    {
      title: 'Thời hạn',
      render: (_, row) => (row.startAt || row.endAt)
        ? <span>{row.startAt ? dayjs(row.startAt).format('DD/MM/YYYY') : '—'} → {row.endAt ? dayjs(row.endAt).format('DD/MM/YYYY') : '—'}</span>
        : 'Không giới hạn',
    },
    {
      title: 'Công khai',
      dataIndex: 'isPublic',
      render: (value) => value
        ? <Tag color="blue">Công khai trong app</Tag>
        : <Tag color="default">Chỉ nhập tay</Tag>,
    },
    { title: 'Trạng thái', dataIndex: 'status', render: statusTag },
    {
      title: '', render: (_, row) => <Space>
        <Button onClick={() => showEdit(row)}>Sửa</Button>
        <Popconfirm
          title={row.status === 'ACTIVE' ? 'Tắt mã này?' : 'Bật mã này?'}
          okText={row.status === 'ACTIVE' ? 'Tắt' : 'Bật'} cancelText="Hủy"
          onConfirm={() => toggleStatus(row)}
        >
          <Button danger={row.status === 'ACTIVE'}>{row.status === 'ACTIVE' ? 'Tắt' : 'Bật'}</Button>
        </Popconfirm>
      </Space>,
    },
  ];

  return <KT01BaseLayout isFullScreen><div className="container-fluid px-4 my-4">
    <Card>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div><Title level={4} className="mb-0">Mã khuyến mãi xe ghép</Title><Text type="secondary">Khách phải nhập đúng mã mới được giảm giá</Text></div>
        <Space><Button onClick={load} loading={loading}>Làm mới</Button><Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} onClick={showCreate}>Tạo mã</Button></Space>
      </div>
      <Table
        rowKey="promoCodeId"
        dataSource={items}
        columns={columns}
        loading={loading}
        scroll={{ x: 1000 }}
        locale={{ emptyText: 'Không có dữ liệu' }}
        pagination={false}
      />
    </Card>
    <Modal
      title={editing ? 'Sửa mã khuyến mãi' : 'Tạo mã khuyến mãi'}
      width={720}
      open={open}
      onCancel={() => setOpen(false)}
      destroyOnClose
      footer={<Space>
        <Button onClick={() => setOpen(false)}>Đóng</Button>
        <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} loading={saving} onClick={save}>Lưu</Button>
      </Space>}
    >
      <Form form={form} layout="vertical" initialValues={defaults}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="code" label="Mã khuyến mãi" extra="Chỉ chữ/số/gạch ngang/gạch dưới, viết hoa tự động" rules={[{ required: true, message: 'Nhập mã' }]}>
              <Input placeholder="Ví dụ: TET2026" style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="description" label="Mô tả (chỉ admin thấy)">
              <Input placeholder="Ví dụ: Khuyến mãi Tết 2026" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="discountType" label="Loại giảm giá" rules={[{ required: true }]}>
              <Select options={[
                { value: 'PERCENT', label: 'Theo phần trăm' },
                { value: 'FIXED', label: 'Số tiền cố định' },
                { value: 'TARGET_FARE', label: 'Đưa tổng tiền về mức cố định' },
              ]} />
            </Form.Item>
          </Col>
          {discountType === 'PERCENT' ? <>
            <Col span={8}>
              <Form.Item name="discountPercent" label="Phần trăm giảm" rules={[{ required: true, message: 'Nhập %' }]}>
                <InputNumber min={0.01} max={100} step={1} addonAfter="%" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxDiscountAmount" label="Giảm tối đa (không bắt buộc)">
                <InputNumber min={0} step={1000} addonAfter="₫" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </> : discountType === 'FIXED' ? (
            <Col span={16}>
              <Form.Item name="discountAmount" label="Số tiền giảm" rules={[{ required: true, message: 'Nhập số tiền' }]}>
                <InputNumber min={1} step={1000} addonAfter="₫" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          ) : (
            <Col span={16}>
              <Form.Item name="targetFare" label="Tổng tiền khách phải trả" rules={[{ required: true, message: 'Nhập giá cuối' }]}>
                <InputNumber min={0} step={1000} addonAfter="₫" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <Form.Item name="routeScope" label="Phạm vi tuyến" rules={[{ required: true }]}>
              <Select options={[
                { value: 'ALL', label: 'Mọi tuyến' },
                { value: 'HANOI_OLD_NAM_DINH', label: 'Hai chiều Hà Nội ↔ khu vực Nam Định cũ' },
              ]} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="minTotalFare" label="Đơn tối thiểu để dùng mã" extra="Để 0 nếu không yêu cầu">
              <InputNumber min={0} step={1000} addonAfter="₫" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="usageLimit" label="Tổng số lần dùng" extra="Để trống = không giới hạn">
              <InputNumber min={1} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="perCustomerLimit" label="Số lần/khách" extra="Để trống = không giới hạn">
              <InputNumber min={1} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="startAt" label="Bắt đầu áp dụng (không bắt buộc)">
              <DatePicker showTime style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="endAt" label="Kết thúc áp dụng (không bắt buộc)">
              <DatePicker showTime style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="isPublic"
              label="Công khai trong app"
              valuePropName="checked"
              extra="Bật để khách hàng thấy mã này trong danh sách khuyến mãi khả dụng khi đặt xe. Tắt nếu mã chỉ dùng nội bộ/CSKH gửi riêng cho từng khách (khách vẫn nhập tay được nếu biết mã)."
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  </div></KT01BaseLayout>;
}

export default RidePromoCodes;
